import { useState, useEffect, useCallback } from 'react'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../lib/firebase'

/**
 * Hook for real-time Firestore collection subscription
 */
export function useCollection(collectionName, options = {}) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true)
    setError(null)

    let q = collection(db, collectionName)

    // Apply ordering
    if (options.orderByField) {
      q = query(q, orderBy(options.orderByField, options.orderDirection || 'asc'))
    }

    // Apply filters
    if (options.where) {
      options.where.forEach(([field, op, value]) => {
        q = query(q, where(field, op, value))
      })
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        setData(docs)
        setLoading(false)
      },
      (err) => {
        console.error(`Firestore error (${collectionName}):`, err)
        setError(err.message)
        setLoading(false)
      }
    )

    return unsubscribe
  }, [collectionName, JSON.stringify(options)])

  return { data, loading, error }
}

/**
 * Hook for real-time single document subscription
 */
export function useDocument(collectionName, documentId) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!documentId) {
      setData(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const docRef = doc(db, collectionName, documentId)

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setData({ id: snapshot.id, ...snapshot.data() })
        } else {
          setData(null)
        }
        setLoading(false)
      },
      (err) => {
        console.error(`Firestore error (${collectionName}/${documentId}):`, err)
        setError(err.message)
        setLoading(false)
      }
    )

    return unsubscribe
  }, [collectionName, documentId])

  return { data, loading, error }
}

/**
 * Hook for Firestore CRUD operations
 */
export function useFirestoreMutations(collectionName) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const add = useCallback(async (data) => {
    setLoading(true)
    setError(null)
    try {
      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      setLoading(false)
      return docRef.id
    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }, [collectionName])

  const update = useCallback(async (documentId, data) => {
    setLoading(true)
    setError(null)
    try {
      const docRef = doc(db, collectionName, documentId)
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      })
      setLoading(false)
    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }, [collectionName])

  const remove = useCallback(async (documentId) => {
    setLoading(true)
    setError(null)
    try {
      const docRef = doc(db, collectionName, documentId)
      await deleteDoc(docRef)
      setLoading(false)
    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }, [collectionName])

  return { add, update, remove, loading, error }
}

/**
 * Get a single document (non-reactive)
 */
export async function getDocument(collectionName, documentId) {
  const docRef = doc(db, collectionName, documentId)
  const snapshot = await getDoc(docRef)
  if (snapshot.exists()) {
    return { id: snapshot.id, ...snapshot.data() }
  }
  return null
}

/**
 * Get all documents from a collection (non-reactive)
 */
export async function getCollection(collectionName) {
  const snapshot = await getDocs(collection(db, collectionName))
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
}

/**
 * Hook for real-time Firestore subcollection subscription
 * e.g., useSubcollection('characters', 'char123', 'notes')
 */
export function useSubcollection(parentCollection, parentId, subcollectionName, options = {}) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!parentId) {
      setData([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const parentRef = doc(db, parentCollection, parentId)
    let q = collection(parentRef, subcollectionName)

    // Apply ordering
    if (options.orderByField) {
      q = query(q, orderBy(options.orderByField, options.orderDirection || 'asc'))
    }

    // Apply filters
    if (options.where) {
      options.where.forEach(([field, op, value]) => {
        q = query(q, where(field, op, value))
      })
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }))
        setData(docs)
        setLoading(false)
      },
      (err) => {
        console.error(`Firestore error (${parentCollection}/${parentId}/${subcollectionName}):`, err)
        setError(err.message)
        setLoading(false)
      }
    )

    return unsubscribe
  }, [parentCollection, parentId, subcollectionName, JSON.stringify(options)])

  return { data, loading, error }
}

/**
 * Hook for Firestore subcollection CRUD operations
 */
export function useSubcollectionMutations(parentCollection, parentId, subcollectionName) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const add = useCallback(async (data) => {
    if (!parentId) throw new Error('Parent document ID is required')

    setLoading(true)
    setError(null)
    try {
      const parentRef = doc(db, parentCollection, parentId)
      const subcollectionRef = collection(parentRef, subcollectionName)
      const docRef = await addDoc(subcollectionRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      setLoading(false)
      return docRef.id
    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }, [parentCollection, parentId, subcollectionName])

  const update = useCallback(async (documentId, data) => {
    if (!parentId) throw new Error('Parent document ID is required')

    setLoading(true)
    setError(null)
    try {
      const parentRef = doc(db, parentCollection, parentId)
      const docRef = doc(parentRef, subcollectionName, documentId)
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      })
      setLoading(false)
    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }, [parentCollection, parentId, subcollectionName])

  const remove = useCallback(async (documentId) => {
    if (!parentId) throw new Error('Parent document ID is required')

    setLoading(true)
    setError(null)
    try {
      const parentRef = doc(db, parentCollection, parentId)
      const docRef = doc(parentRef, subcollectionName, documentId)
      await deleteDoc(docRef)
      setLoading(false)
    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }, [parentCollection, parentId, subcollectionName])

  return { add, update, remove, loading, error }
}
